// ─── Revenue & Spend — the money maths, in ONE place ─────────────────────────
// Every figure on the tab goes through these helpers. The alternative is each
// section normalising a quarterly bill its own way and the subtotals quietly
// disagreeing with the headline; this file exists so that cannot happen.

// ── PERIODS ──────────────────────────────────────────────────────────────────
// Every cost normalises to a PER-DAY rate and multiplies up, so a quarterly
// £2,500 bill shows a sensible daily figure rather than a spike in one month
// and nothing either side of it.
export const PERIODS = [
  { id: 'day',   label: 'Day',       days: 1 },
  { id: 'week',  label: 'Week',      days: 7 },
  { id: 'month', label: 'Month',     days: 30.44 },
  { id: 'q',     label: '3 months',  days: 91.31 },
  { id: 'half',  label: '6 months',  days: 182.62 },
  { id: 'year',  label: '12 months', days: 365 },
];

// Occurrences per year. MUST match OCCURRENCES_PER_YEAR in
// backend/src/routes/admin.js — the backend computes monthlyEquivalent from the
// same map, and two different ideas of "quarterly" is how a subtotal and a
// headline start disagreeing by a factor of three.
export const OCCURRENCES_PER_YEAR = { weekly: 365 / 7, monthly: 12, quarterly: 4, yearly: 1 };

export const FREQUENCY_LABELS = {
  weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly',
};

export const CURRENCIES = ['AUD', 'USD', 'GBP'];

// ── HOW OFTEN A SALE HAPPENS ─────────────────────────────────────────────────
// Ben thinks "one a week" or "three a month", not "52 a year". Asking him for
// the annual figure is arithmetic he should not have to do in his head, and
// getting it wrong is silent — 52 typed as 12 just quietly understates the year.
//
// BOTH the number AND the rate are stored, never just the derived annual total.
// Reloading to find "36" where he typed "3 per month" would lose what he meant.
export const SALES_RATES = {
  week:    { label: 'per week',    perYear: 52 },
  month:   { label: 'per month',   perYear: 12 },
  quarter: { label: 'per quarter', perYear: 4 },
  year:    { label: 'per year',    perYear: 1 },
};

/**
 * Sales a year, from a rate. Fractions are fine — 1 per quarter is 4 a year.
 *
 * ⚠ AN OFFER WITHOUT A RATE IS A YEARLY COUNT. Rows saved before rates existed
 * hold a plain annual number, and reading one of those as "per week" would
 * multiply Ben's plan by 52 without a word. Absent means 'year', always.
 */
export function yearlyCount(offer) {
  const n = Number(offer.count) || 0;
  const rate = SALES_RATES[offer.rate] || SALES_RATES.year;
  return n * rate.perYear;
}

/** "3 per month — 36 a year", so the rate and the annual picture sit together. */
export function rateSummary(offer) {
  const n = Number(offer.count) || 0;
  const rate = SALES_RATES[offer.rate] || SALES_RATES.year;
  const yearly = yearlyCount(offer);
  const tidy = Math.round(yearly * 100) / 100;
  if (offer.rate === 'year' || !offer.rate) return `${tidy} a year`;
  return `${n} ${rate.label} — ${tidy} a year`;
}

// ── THE DEFAULT RATES ────────────────────────────────────────────────────────
// Starting points only — all four are editable and saved to
// ry_business_plan.payload.rates. There is no live FX lookup anywhere.
export const DEFAULT_RATES = {
  outUsdAud: 1.42,      // what a US$1 bill actually costs
  outGbpAud: 1.88,      // what a £1 bill actually costs
  foreignFeePct: 3,     // the bank's foreign transaction fee
  // What LANDS when US$1 is charged. Stripe's cut is ALREADY OUT of this
  // figure — it comes from a real payment (US$75 charged, A$97.87 received).
  // Nothing anywhere may deduct fees again on top of it.
  inUsdAud: 1.30,
};

/**
 * MONEY GOING OUT. A foreign bill costs the market rate PLUS the bank's fee.
 * @returns {number} the cost in AUD
 */
export function outToAud(amount, currency, rates) {
  const n = Number(amount) || 0;
  if (currency === 'AUD') return n;
  const rate = currency === 'GBP' ? Number(rates.outGbpAud) : Number(rates.outUsdAud);
  return n * (rate || 0) * (1 + (Number(rates.foreignFeePct) || 0) / 100);
}

/**
 * MONEY COMING IN. Ben charges in USD, Stripe converts and takes its cut before
 * anything lands. inUsdAud already has that cut removed — do NOT deduct again.
 * @returns {number} what lands, in AUD
 */
export function inToAud(amount, currency, rates) {
  const n = Number(amount) || 0;
  return currency === 'AUD' ? n : n * (Number(rates.inUsdAud) || 0);
}

/** What one cost works out at per day, in AUD. */
export function costPerDayAud(cost, rates) {
  const per = OCCURRENCES_PER_YEAR[cost.frequency];
  if (!per) return 0;
  return (outToAud(cost.amount, cost.currency, rates) * per) / 365;
}

/** Display-only currency switch. Never changes what is stored. */
export function display(aud, view, rates) {
  if (view === 'USD') {
    const r = Number(rates.outUsdAud) || 1;
    return r ? aud / r : 0;
  }
  return aud;
}

/**
 * WHOLE DOLLARS, ALWAYS. There is deliberately no decimal-places argument: a
 * caller that could ask for cents is a caller that will eventually leak them
 * back onto the screen. The one exception on this tab is the four exchange
 * rates, which are raw number inputs and never pass through here — they need
 * their two decimals to be usable.
 *
 * ROUNDING IS A DISPLAY STEP AND ONLY A DISPLAY STEP. Do the arithmetic on
 * exact values and call this once, at the end. Summing already-rounded figures
 * would drift a total away from what Ben actually pays, which is why a group's
 * rows may occasionally look a dollar off their own subtotal — the subtotal is
 * the honest one.
 */
export function money(aud, view, rates) {
  const v = display(aud, view, rates);
  if (!Number.isFinite(v)) return '—';
  // Math.round is half-up, which is what ".50 and above goes up" means.
  return (view === 'USD' ? 'US$' : 'A$') +
    Math.round(v).toLocaleString('en-AU', { maximumFractionDigits: 0 });
}

export const periodDays = (id) => (PERIODS.find(p => p.id === id) || PERIODS[2]).days;
export const periodLabel = (id) => (PERIODS.find(p => p.id === id) || PERIODS[2]).label.toLowerCase();

/** Days since an ISO date, or null. Used for the ledger-staleness line. */
export function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(`${String(iso).slice(0, 10)}T00:00:00Z`).getTime()) / 86400000);
}

/**
 * The 12-month projection. PROJECTED MONEY ONLY — nothing actual is allowed
 * into this function, and nothing it returns may share a card, a row, a total
 * or a chart series with a real figure. Actual revenue is currently near zero
 * and a projection sitting beside it looking similar would make the whole tab
 * untrustworthy.
 *
 * @param {Array} offers - planner rows
 * @param {number} outPerDayAud - everything going out, per day
 * @param {boolean} spreadAll - sales spread across 12 months, or all in month 1
 */
export function projectYear(offers, outPerDayAud, spreadAll, rates) {
  const monthly = Array.from({ length: 12 }, () => 0);
  for (const o of offers) {
    const landed = inToAud(o.price, o.currency, rates);
    const count = yearlyCount(o);          // rate -> sales a year
    if (!count) continue;
    for (let signM = 0; signM < 12; signM++) {
      const signed = spreadAll ? count / 12 : (signM === 0 ? count : 0);
      if (!signed) continue;
      if (o.plan === 'upfront') {
        monthly[signM] += signed * landed;
      } else if (o.plan === 'split') {
        const n = Math.max(1, Number(o.months) || 1);
        for (let k = 0; k < n && signM + k < 12; k++) monthly[signM + k] += (signed * landed) / n;
      } else {
        const n = Math.max(1, Number(o.months) || 1);
        for (let k = 0; k < n && signM + k < 12; k++) monthly[signM + k] += signed * landed;
      }
    }
  }
  const costMonth = outPerDayAud * 30.44;
  let cum = 0;
  return monthly.map((inn, i) => {
    const net = inn - costMonth;
    cum += net;
    return { m: `M${i + 1}`, inn, out: costMonth, net, cum };
  });
}

/** What a single planner row lands in a year, in AUD. */
export function offerYearTotal(o, rates) {
  const landed = inToAud(o.price, o.currency, rates);
  const count = yearlyCount(o);
  const months = Math.max(1, Number(o.months) || 1);
  return o.plan === 'retainer' ? count * landed * months : count * landed;
}

// Ben's real pricing. Used ONLY when no saved plan exists — a saved plan
// always wins, and nothing here migrates or overwrites one.
//
// EVERY OFFER HERE IS CHARGED IN US DOLLARS, the RY course included.
// backend/src/routes/checkout.js settles it: the course charges
// RY_COURSE_PRICE_CENTS (default 6900) with currency 'usd', and tags the charge
// metadata price_usd. So it is US$69, not A$69.
//
// This line said AUD from the day the planner was built, taken from a spec that
// described the course as "A$69". Seeded as AUD it understated what lands per
// sale, because a US$69 charge arrives as roughly A$90 after conversion. If it
// ever looks wrong again, read checkout.js rather than a spec — the code that
// takes the money is the only authority on what currency it takes it in.
//
// Rates are the unit each offer is naturally thought about in.
export const SEED_OFFERS = [
  { id: 'o1', name: 'RY course',                    count: 1, rate: 'week',  price: 69,   currency: 'USD', plan: 'upfront',  months: 1 },
  { id: 'o2', name: 'UUU — US$2,400 over 4 months', count: 1, rate: 'month', price: 2400, currency: 'USD', plan: 'split',    months: 4 },
  { id: 'o3', name: 'UUU — US$2,400 over 6 months', count: 1, rate: 'month', price: 2400, currency: 'USD', plan: 'split',    months: 6 },
  { id: 'o4', name: 'UUU — US$2,000 over 6 months', count: 1, rate: 'month', price: 2000, currency: 'USD', plan: 'split',    months: 6 },
  { id: 'o5', name: 'UUU — US$2,400 up front',      count: 1, rate: 'month', price: 2400, currency: 'USD', plan: 'upfront',  months: 1 },
  { id: 'o6', name: 'Messaging retainer',           count: 2, rate: 'year',  price: 75,   currency: 'USD', plan: 'retainer', months: 12 },
];
